import { cn } from '@e-kyc/utils';

export enum IconName {
  Check = 'check',
  Close = 'close',
  Devices = 'devices',
  EventAvailable = 'event_available',
  ExpandMore = 'expand_more',
  SentimentDissatisfied = 'sentiment_dissatisfied',
  SentimentSatisfiedAlt = 'sentiment_satisfied_alt',
  Visibility = 'visibility',
  VisibilityOff = 'visibility_off',
  CameraAlt = 'camera_alt',
  CheckCircle = 'check_circle',
  Warning = 'warning',
}

interface IconProps extends React.HTMLAttributes<HTMLDivElement> {
  icon: IconName;
  className?: string;
  variant?: 'outlined' | 'rounded';
}

export function Icon({ icon, className, variant = 'outlined', ...props }: IconProps) {
  return (
    <div className={cn('text-[24px] !leading-0', className)} {...props}>
      <i
        className={cn(variant === 'outlined' ? 'material-icons-outlined' : 'material-icons-round')}
      >
        {icon}
      </i>
    </div>
  );
}

function createIconComponent(icon: IconName, variant?: 'outlined' | 'rounded') {
  return function IconComponent(props: Omit<IconProps, 'icon'>) {
    return <Icon icon={icon} {...props} variant={variant} />;
  };
}

// Rounded Icons
export const CheckCircleRoundedIcon = createIconComponent(IconName.CheckCircle, 'rounded');

// Outlined Icons
export const CameraAltOutlinedIcon = createIconComponent(IconName.CameraAlt, 'outlined');
export const DevicesOutlinedIcon = createIconComponent(IconName.Devices, 'outlined');
export const EventAvailableOutlinedIcon = createIconComponent(IconName.EventAvailable, 'outlined');
export const ExpandMoreOutlinedIcon = createIconComponent(IconName.ExpandMore, 'outlined');
export const SentimentDissatisfiedOutlinedIcon = createIconComponent(
  IconName.SentimentDissatisfied,
  'outlined',
);
export const SentimentSatisfiedAltOutlinedIcon = createIconComponent(
  IconName.SentimentSatisfiedAlt,
  'outlined',
);
export const VisibilityOutlinedIcon = createIconComponent(IconName.Visibility, 'outlined');
export const VisibilityOffOutlinedIcon = createIconComponent(IconName.VisibilityOff, 'outlined');

// Rounded Icons
export const CheckRoundedIcon = createIconComponent(IconName.Check, 'rounded');
export const CloseRoundedIcon = createIconComponent(IconName.Close, 'rounded');
export const WarningRoundedIcon = createIconComponent(IconName.Warning, 'rounded');

// SVG Icons
export function IDCardIcon({ className, ...props }: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      className={className}
      width='24'
      height='24'
      fill='none'
      viewBox='0 0 24 24'
      {...props}
    >
      <path
        fill='currentColor'
        fillRule='evenodd'
        d='M1 7.275C1 6.024 1.979 5 3.175 5h17.733c1.196 0 2.175 1.024 2.175 2.275v9.45c0 1.251-.979 2.275-2.175 2.275H3.175C1.979 19 1 17.976 1 16.725zM20.908 6.05H3.175c-.658 0-1.171.537-1.171 1.225v9.45c0 .688.513 1.225 1.17 1.225h17.734c.657 0 1.171-.537 1.171-1.225v-9.45c0-.688-.514-1.225-1.171-1.225m-6.325 3.233a.54.54 0 0 1 .553-.525h4.416a.54.54 0 0 1 .552.525v5.6a.54.54 0 0 1-.552.525h-4.416a.54.54 0 0 1-.553-.525zM19 9.808h-3.312v4.55H19zm-13.772.027c-.104-.002-.203.054-.277.155a.67.67 0 0 0-.115.386c0 .145.042.284.115.385s.173.158.277.156h7.187c.103.002.202-.054.276-.156a.67.67 0 0 0 .115-.385.67.67 0 0 0-.115-.386c-.074-.101-.173-.157-.276-.155zm-.276 3.403c.073-.102.173-.158.276-.155h7.186c.103-.003.203.053.277.155.073.102.115.241.115.386a.67.67 0 0 1-.115.386c-.074.102-.174.158-.277.155H5.228c-.103.003-.203-.053-.276-.155a.67.67 0 0 1-.116-.386c0-.145.042-.284.116-.386'
        clipRule='evenodd'
      ></path>
      <path
        fill='currentColor'
        d='M14.583 9.283h-.269zm0 5.6h-.269zm1.104-5.075v-.27h-.269v.27zm3.313 0h.27v-.27H19zm-3.312 4.55h-.27v.269h.27zm3.312 0v.269h.27v-.27zM4.95 9.99l-.218-.157zm.277-.155-.006.269h.006zm-.277.926.219-.157zm.277.156v-.269h-.006zm7.187 0 .005-.269h-.005zm.276-.156-.218-.157zm0-.77.218-.158zm-.276-.156v.269h.005zm-7.187 3.248-.006.27h.006zm-.276.155.218.158zm7.462-.155v.27l.006-.001zm.277.155.218-.158zm0 .772.217.158zm-.277.155.006-.269h-.006zm-7.186 0v-.269h-.005zm-.276-.155-.218.158zM3.175 4.73C1.819 4.73.73 5.888.73 7.276h.538c0-1.114.87-2.006 1.906-2.006zm17.733 0H3.175v.54h17.733zm2.444 2.545c0-1.388-1.088-2.544-2.444-2.544v.538c1.036 0 1.906.892 1.906 2.006zm0 9.45v-9.45h-.538v9.45zm-2.444 2.544c1.356 0 2.444-1.156 2.444-2.544h-.538c0 1.114-.87 2.006-1.906 2.006zm-17.733 0h17.733v-.538H3.175zM.73 16.725c0 1.388 1.088 2.544 2.444 2.544v-.538c-1.036 0-1.906-.892-1.906-2.006zm0-9.45v9.45h.538v-9.45zm2.444-.956h17.733v-.538H3.175zm-.902.956c0-.55.404-.956.902-.956v-.538c-.818 0-1.44.67-1.44 1.494zm0 9.45v-9.45h-.538v9.45zm.902.956c-.498 0-.902-.405-.902-.956h-.538c0 .825.622 1.494 1.44 1.494zm17.733 0H3.175v.538h17.733zm.902-.956c0 .55-.405.956-.902.956v.538c.817 0 1.44-.669 1.44-1.494zm0-9.45v9.45h.538v-9.45zm-.902-.956c.497 0 .902.406.902.956h.538c0-.825-.623-1.494-1.44-1.494zm-5.773 2.17c-.44 0-.82.343-.82.794h.538c0-.129.114-.256.283-.256zm4.417 0h-4.416v.538h4.416zm.821.794c0-.451-.38-.794-.821-.794v.538c.169 0 .283.127.283.256zm0 5.6v-5.6h-.538v5.6zm-.821.794c.44 0 .821-.343.821-.794h-.538c0 .128-.114.255-.283.255zm-4.416 0h4.416v-.539h-4.416zm-.822-.794c0 .45.38.794.822.794v-.539c-.17 0-.283-.127-.283-.256zm0-5.6v5.6h.539v-5.6zm1.374.794H19V9.54h-3.312zm.269 4.28V9.809h-.539v4.55zM19 14.09h-3.312v.538H19zm-.27-4.281v4.55h.54v-4.55zm-13.56.34a.14.14 0 0 1 .04-.04l.01-.004h.002l.011-.538a.6.6 0 0 0-.5.267zm-.064.228a.4.4 0 0 1 .064-.228l-.437-.315a.94.94 0 0 0-.166.543zm.064.228a.4.4 0 0 1-.064-.228h-.539c0 .193.055.389.166.543zm.052.044H5.22l-.01-.004a.14.14 0 0 1-.04-.04l-.437.315a.6.6 0 0 0 .5.267zm7.193 0H5.228v.539h7.187zm.058-.044a.14.14 0 0 1-.041.04l-.01.004h-.002l-.01.538a.6.6 0 0 0 .5-.267zm.063-.228a.4.4 0 0 1-.063.228l.436.315a.93.93 0 0 0 .166-.543zm-.063-.228a.4.4 0 0 1 .063.228h.539a.94.94 0 0 0-.166-.543zm-.053-.044h.003l.009.004a.14.14 0 0 1 .04.04l.437-.315a.6.6 0 0 0-.5-.267zm-7.192 0h7.187v-.538H5.228zm.007 2.71a.6.6 0 0 0-.502.267l.437.315a.14.14 0 0 1 .04-.04l.01-.004h.002zm7.179 0H5.228v.538h7.186zm.495.266a.6.6 0 0 0-.502-.266l.013.538h.003l.008.004a.14.14 0 0 1 .041.04zm.166.544a.94.94 0 0 0-.166-.544l-.437.316a.4.4 0 0 1 .065.228zm-.166.544a.94.94 0 0 0 .166-.544h-.538a.4.4 0 0 1-.065.228zm-.501.267a.6.6 0 0 0 .5-.267l-.435-.316a.14.14 0 0 1-.042.04l-.009.004h-.002zm-7.18 0h7.186v-.539H5.228zm-.494-.267c.113.156.29.271.5.267l-.011-.539H5.22l-.01-.004a.14.14 0 0 1-.04-.04zm-.167-.544c0 .193.055.39.167.544l.436-.316a.4.4 0 0 1-.064-.228zm.167-.544a.93.93 0 0 0-.167.544h.539c0-.098.028-.179.064-.228z'
      ></path>
    </svg>
  );
}

export function ArOnYouIcon({ className, ...props }: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      className={className}
      width='24'
      height='24'
      fill='none'
      viewBox='0 0 24 24'
      {...props}
    >
      <path
        fill='currentColor'
        fillRule='evenodd'
        d='M6 5a1 1 0 0 0-1 1v2a1 1 0 0 1-2 0V6a3 3 0 0 1 3-3h2a1 1 0 0 1 0 2zm9-1a1 1 0 0 1 1-1h2a3 3 0 0 1 3 3v2a1 1 0 1 1-2 0V6a1 1 0 0 0-1-1h-2a1 1 0 0 1-1-1m-7 6a1 1 0 0 1 1-1h.01a1 1 0 0 1 0 2H9a1 1 0 0 1-1-1m6 0a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H15a1 1 0 0 1-1-1m-5.2 4.286a1 1 0 0 1 1.414.014 2.5 2.5 0 0 0 3.572 0 1 1 0 0 1 1.428 1.4 4.5 4.5 0 0 1-6.428 0 1 1 0 0 1 .014-1.414M4 15a1 1 0 0 1 1 1v2a1 1 0 0 0 1 1h2a1 1 0 1 1 0 2H6a3 3 0 0 1-3-3v-2a1 1 0 0 1 1-1m16 0a1 1 0 0 1 1 1v2a3 3 0 0 1-3 3h-2a1 1 0 1 1 0-2h2a1 1 0 0 0 1-1v-2a1 1 0 0 1 1-1'
        clipRule='evenodd'
      ></path>
    </svg>
  );
}

export function ExclamationIcon({ className, ...props }: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      width='57'
      height='56'
      viewBox='0 0 57 56'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      className={className}
      {...props}
    >
      <path
        d='M27.8895 19.1123C28.9857 19.1123 29.8743 20.001 29.8743 21.0971V29.0364C29.8743 30.1325 28.9857 31.0212 27.8895 31.0212C26.7933 31.0212 25.9047 30.1325 25.9047 29.0364V21.0971C25.9047 20.001 26.7933 19.1123 27.8895 19.1123Z'
        fill='#F59F00'
      />
      <path
        d='M25.9047 36.9756C25.9047 35.8794 26.7933 34.9908 27.8895 34.9908H27.9094C29.0055 34.9908 29.8942 35.8794 29.8942 36.9756C29.8942 38.0717 29.0055 38.9604 27.9094 38.9604H27.8895C26.7933 38.9604 25.9047 38.0717 25.9047 36.9756Z'
        fill='#F59F00'
      />
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M27.8891 7C26.8657 7 25.8596 7.26376 24.9678 7.76582C24.0761 8.26788 23.3288 8.9913 22.798 9.86629L22.7923 9.87573L5.98106 37.9408L5.96494 37.9683C5.44503 38.8686 5.16993 39.8895 5.16702 40.9291C5.1641 41.9689 5.43348 42.9912 5.94835 43.8945C6.46321 44.7977 7.20562 45.5505 8.1017 46.0777C8.99778 46.605 10.0163 46.8885 11.056 46.8999L11.0778 46.9H44.7003L44.7222 46.8999C45.7618 46.8885 46.7803 46.605 47.6764 46.0777C48.5725 45.5505 49.3149 44.7977 49.8298 43.8945C50.3446 42.9912 50.614 41.9689 50.6111 40.9291C50.6082 39.8895 50.3331 38.8686 49.8132 37.9683L49.7971 37.9408L32.9858 9.87573L32.9801 9.86629C32.4493 8.9913 31.702 8.26788 30.8103 7.76582C29.9185 7.26376 28.9124 7 27.8891 7ZM26.9153 11.2249C27.2126 11.0575 27.5479 10.9696 27.8891 10.9696C28.2302 10.9696 28.5655 11.0575 28.8628 11.2249C29.1587 11.3915 29.4069 11.6312 29.5837 11.9211L29.5861 11.925L46.3822 39.9649C46.5512 40.2621 46.6406 40.5981 46.6415 40.9403C46.6425 41.2868 46.5527 41.6276 46.3811 41.9287C46.2094 42.2298 45.962 42.4807 45.6633 42.6565C45.3672 42.8307 45.031 42.925 44.6876 42.9304H11.0905C10.7471 42.925 10.4109 42.8307 10.1148 42.6565C9.81614 42.4807 9.56867 42.2298 9.39705 41.9287C9.22543 41.6276 9.13564 41.2868 9.13661 40.9403C9.13756 40.5981 9.22696 40.2621 9.39602 39.9647L26.1945 11.9211C26.3712 11.6312 26.6194 11.3915 26.9153 11.2249Z'
        fill='#F59F00'
      />
    </svg>
  );
}

export function WarningRoundedOutlinedIcon({ className, ...props }: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      className={className}
      width={24}
      height={24}
      viewBox='0 0 48 48'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      {...props}
    >
      <path
        d='M23.9764 16.382C24.916 16.382 25.6777 17.1437 25.6777 18.0833V24.8883C25.6777 25.8279 24.916 26.5896 23.9764 26.5896C23.0369 26.5896 22.2752 25.8279 22.2752 24.8883V18.0833C22.2752 17.1437 23.0369 16.382 23.9764 16.382Z'
        fill='currentColor'
      />
      <path
        d='M22.2752 31.6933C22.2752 30.7538 23.0369 29.9921 23.9764 29.9921H23.9935C24.933 29.9921 25.6947 30.7538 25.6947 31.6933C25.6947 32.6329 24.933 33.3946 23.9935 33.3946H23.9764C23.0369 33.3946 22.2752 32.6329 22.2752 31.6933Z'
        fill='currentColor'
      />
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        fill='currentColor'
        d='M23.9761 6C23.0989 6 22.2365 6.22608 21.4722 6.65642C20.7078 7.08676 20.0673 7.70683 19.6123 8.45682L19.6074 8.46491L5.19777 32.5207L5.18395 32.5442C4.73831 33.316 4.50252 34.191 4.50002 35.0821C4.49752 35.9733 4.72842 36.8496 5.16973 37.6238C5.61104 38.3981 6.24739 39.0432 7.01547 39.4952C7.78354 39.9471 8.65656 40.1901 9.54769 40.1999L9.56638 40.2H38.3857L38.4044 40.1999C39.2956 40.1901 40.1686 39.9471 40.9367 39.4952C41.7047 39.0432 42.3411 38.3981 42.7824 37.6238C43.2237 36.8496 43.4546 35.9733 43.4521 35.0821C43.4496 34.191 43.2138 33.316 42.7682 32.5442L42.7543 32.5207L28.3447 8.46491L28.3398 8.45682C27.8849 7.70683 27.2443 7.08676 26.48 6.65642C25.7156 6.22608 24.8532 6 23.9761 6ZM23.1414 9.62133C23.3962 9.47788 23.6837 9.40252 23.9761 9.40252C24.2684 9.40252 24.5559 9.47788 24.8107 9.62133C25.0643 9.76412 25.2771 9.9696 25.4286 10.2181L25.4306 10.2215L39.8273 34.2556C39.9722 34.5104 40.0488 34.7984 40.0496 35.0917C40.0504 35.3887 39.9735 35.6808 39.8264 35.9389C39.6792 36.197 39.4671 36.412 39.2111 36.5627C38.9573 36.712 38.6692 36.7929 38.3748 36.7975H9.57727C9.28295 36.7929 8.99479 36.712 8.74101 36.5627C8.48499 36.412 8.27287 36.197 8.12576 35.9389C7.97866 35.6808 7.9017 35.3887 7.90253 35.0917C7.90335 34.7984 7.97998 34.5103 8.12489 34.2555L22.5235 10.2181C22.6751 9.9696 22.8878 9.76412 23.1414 9.62133Z'
      />
    </svg>
  );
}
